import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useCreateCropSetting, CreateCropSettingValidation } from "@/lib/api/risk/profiles/cropSettings";
import { CropSettingsForm, type CreateFormData } from "./CropSettingsForm";

interface CreateCropSettingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateCropSettingDialog({ isOpen, onClose, onSuccess }: CreateCropSettingDialogProps) {
  const { toast } = useToast();
  const createCropSetting = useCreateCropSetting();
  const [isCreating, setIsCreating] = useState(false);

  const handleSuccess = () => {
    onSuccess();
    onClose();
  };

  const handleSubmit = async (data: CreateFormData) => {
    setIsCreating(true);
    try {
      // Validate data with Zod schema before sending to API
      const validationResult = CreateCropSettingValidation.safeParse(data);

      if (!validationResult.success) {
        const errorMessages = validationResult.error.errors.map(err =>
          `${err.path.join('.')}: ${err.message}`
        ).join(', ');
        toast({
          title: "Invalid form data",
          description: errorMessages,
          variant: "destructive"
        });
        return;
      }

      const result = await createCropSetting(validationResult.data);

      if (!result.success && result.message.includes("already exists")) {
        toast({ title: "Crop setting already exists", variant: "destructive" });
        return;
      }
      if (!result.success) {
        throw result;
      }

      toast({ title: "Crop setting created successfully" });
      handleSuccess();
    } catch (error) {
      console.error("Failed to create crop setting", error);
      toast({
        title: "Failed to create crop setting",
        description: error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Crop Setting</DialogTitle>
          <DialogDescription>
            Configure settings for a specific commodity and crop year combination.
          </DialogDescription>
        </DialogHeader>

        <CropSettingsForm
          mode="create"
          onSubmit={handleSubmit}
          onCancel={onClose}
          showCancelButton={true}
          isSubmitting={isCreating}
        />
      </DialogContent>
    </Dialog>
  );
}
